import { DialogComponent } from "@theme/dialog";
import { sectionRenderer } from "@theme/section-renderer";
import { fetchConfig, localeUrl, withDistributorIdProperty } from "@theme/utilities";
import { formatMoney } from "@theme/money-formatting";
import { CartAddEvent, CartUpdateEvent, SubscriptionOpenEvent } from "@theme/events";

const SECTION_ID = "subscription-drawer";

/**
 * Global subscription drawer. A singleton modal — like the cart drawer —
 * opened from product cards, hydrate-PLP cards, and cart line items.
 *
 * Opening is event-driven: any surface dispatches a `SubscriptionOpenEvent` on `document`
 * (see events.js) and this singleton opens with the supplied context. This mirrors how the
 * cart drawer opens via CartAddEvent — triggers announce intent and stay decoupled from the
 * drawer. The event detail:
 *   - handle      (required) product handle, used to fetch the body section.
 *   - variantId   (optional) preferred variant; the section resolves the eligible variant
 *                  when omitted (e.g. hydrate-PLP cards with no variant id).
 *   - mode        'add' (default) | 'convert'
 *   - lineKey     (convert) the cart line key to change.
 *   - quantity    (convert) the current line quantity.
 *
 * The body (product summary + frequency radios) is fetched on demand from
 * sections/subscription-drawer.liquid via the Section Rendering API and injected into
 * [ref="body"]. "Set up subscription" then adds (/cart/add) or converts (/cart/change)
 * the chosen selling plan and emits the matching cart event.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} body
 * @property {HTMLElement} loading
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} confirmButton
 * @property {HTMLElement} confirmText
 * @property {HTMLElement} confirmSpinner
 *
 * @extends {DialogComponent}
 */
class SubscriptionDrawer extends DialogComponent {
  /** @type {{ handle: string, variantId: string, mode: string, lineKey: string, quantity: number } | null} */
  #context = null;

  /** @type {{ format: string, currency: string, cashbackIcon: string }} Money config from the body section, for scaling convert-mode rewards. */
  #money = { format: "{{amount}}", currency: "USD", cashbackIcon: "" };

  /**
   * Typed view of the base component's refs. The base types `refs` as a generic
   * `Element` map; the drawer's refs are all present in the static shell markup, so we
   * narrow to the local Refs shape here (same casting approach as cart-drawer.js).
   * @returns {Refs}
   */
  get #els() {
    return /** @type {any} */ (this.refs);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(SubscriptionOpenEvent.eventName, this.#onOpenEvent);
    this.#els.body?.addEventListener("change", this.#onBodyChange);
    this.#els.body?.addEventListener("click", this.#onBodyClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(SubscriptionOpenEvent.eventName, this.#onOpenEvent);
  }

  /** @param {Event} event */
  #onOpenEvent = event => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail?.handle) return;
    this.open(detail);
  };

  /**
   * Opens the drawer for a product and fetches its body.
   * @param {{ handle: string, variantId?: string, mode?: string, lineKey?: string, quantity?: number, bcrsFeeVariantId?: string }} context
   */
  async open(context) {
    this.#context = {
      handle: context.handle,
      variantId: context.variantId ?? "",
      mode: context.mode === "convert" ? "convert" : "add",
      lineKey: context.lineKey ?? "",
      quantity: context.quantity ?? 1,
      bcrsFeeVariantId: context.bcrsFeeVariantId ?? "",
    };

    this.#setError("");
    this.#setBodyLoading(true);
    this.#setConfirmEnabled(false);
    this.showDialog();

    await this.#loadBody();
  }

  close() {
    this.closeDialog();
  }

  async #loadBody() {
    const ctx = this.#context;
    if (!ctx) return;

    // localeUrl keeps the Section Rendering fetch on the shopper's language. Without the
    // locale root a secondary-locale page (e.g. /en on a Malay-primary store) would fetch
    // /products/<handle> and get the body rendered in the store's PRIMARY language (MA-2449).
    const url = new URL(localeUrl(`/products/${encodeURIComponent(ctx.handle)}`), window.location.origin);
    if (ctx.variantId) url.searchParams.set("variant", ctx.variantId);

    try {
      const html = await sectionRenderer.getSectionHTML(SECTION_ID, false, url);
      const doc = new DOMParser().parseFromString(html, "text/html");
      const bodyContent = doc.querySelector("[data-subscription-drawer-body]");

      if (!(bodyContent instanceof HTMLElement)) {
        throw new Error("Subscription drawer body not found in section response");
      }

      // Money config for client-side reward scaling.
      this.#money = {
        format: bodyContent.dataset.moneyFormat || "{{amount}}",
        currency: bodyContent.dataset.currency || "USD",
        cashbackIcon: bodyContent.dataset.cashbackIcon || "",
      };


      // Lift the section body's children and state onto refs.body rather than nesting the
      // section's own wrapper inside it — keeps a single body element in the DOM. refs.body
      // is the persistent mount that carries the change/click listeners.
      const { body } = this.#els;
      body.dataset.hasPlans = bodyContent.dataset.hasPlans ?? "false";
      body.dataset.variantId = bodyContent.dataset.variantId ?? "";
      body.replaceChildren(...bodyContent.childNodes);
      this.#setBodyLoading(false);

      if (body.dataset.hasPlans !== "true") {
        this.#setConfirmEnabled(false);
        return;
      }

      this.#applyContextToBody(body);
      this.#syncHeaderPrice();
      this.#setConfirmEnabled(true);
    } catch (error) {
      console.error("Subscription drawer: failed to load options.", error);
      this.#setBodyLoading(false);
      this.#setError(Theme?.translations?.error ?? "Something went wrong. Please try again.");
    }
  }

  /**
   * Applies open-context state to the freshly injected body. In convert mode the quantity starts
   * at the cart line's quantity (add mode keeps the section's default of 1) and the rewards are
   * rendered from the line's per-unit values. Rewards then track the quantity stepper.
   * @param {HTMLElement} body
   */
  #applyContextToBody(body) {
    const ctx = this.#context;
    if (!ctx) return;

    if (ctx.mode === "convert") {
      const input = body.querySelector("[data-drawer-qty-input]");
      if (input instanceof HTMLInputElement) {
        input.value = String(Math.max(1, ctx.quantity));
      }
    }
    this.#updateRewards();
  }

  /**
   * Scales the quantity-dependent rewards in the rendered product-rewards snippet: cashback amount
   * (variant price × cashback % × qty), BV and IBV (per-unit × qty). UC/SRP are unit prices and are
   * left untouched. Reads the per-unit values from `[data-rewards-root]` (data-price /
   * data-cashback-percent / data-bv / data-ibv) and writes the scaled values into the snippet's
   * display spans. No-op when the snippet rendered no rewards (customer not eligible).
   */
  #updateRewards() {
    const root = this.#els.body.querySelector("[data-rewards-root]");
    if (!(root instanceof HTMLElement)) return;

    const qty = this.#getQty();

    const price = parseFloat(root.dataset.price || "0");
    const pct = parseFloat(root.dataset.cashbackPercent || "0");
    const cashbackEl = root.querySelector("[data-rewards-cashback-amount]");
    if (cashbackEl instanceof HTMLElement && price > 0 && pct > 0) {
      const perUnitCents = Math.round((price * pct) / 100);
      cashbackEl.textContent = formatMoney(perUnitCents * qty, this.#money.format, this.#money.currency);
    }

    const bv = parseFloat(root.dataset.bv || "0");
    const bvEl = root.querySelector("[data-rewards-bv-value]");
    if (bvEl instanceof HTMLElement && bv > 0) bvEl.textContent = (bv * qty).toFixed(2);

    const ibv = parseFloat(root.dataset.ibv || "0");
    const ibvEl = root.querySelector("[data-rewards-ibv-value]");
    if (ibvEl instanceof HTMLElement && ibv > 0) ibvEl.textContent = (ibv * qty).toFixed(2);
  }

  /** @param {Event} event */
  #onBodyChange = event => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === "subscription-frequency") {
      this.#syncHeaderPrice();
    }
    if (target instanceof HTMLInputElement && target.dataset.drawerQtyInput != null) {
      this.#clampQty();
      this.#updateRewards();
    }
  };

  /** @param {MouseEvent} event */
  #onBodyClick = event => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest("[data-drawer-qty-plus]")) {
      this.#stepQty(1);
    } else if (target.closest("[data-drawer-qty-minus]")) {
      this.#stepQty(-1);
    }
  };

  #getQty() {
    const input = this.#els.body.querySelector("[data-drawer-qty-input]");
    return input instanceof HTMLInputElement ? Number(input.value) || 1 : 1;
  }

  /** @param {number} delta */
  #stepQty(delta) {
    const input = this.#els.body.querySelector("[data-drawer-qty-input]");
    if (!(input instanceof HTMLInputElement)) return;
    input.value = String(Math.max(1, (Number(input.value) || 1) + delta));
    this.#updateRewards();
  }

  #clampQty() {
    const input = this.#els.body.querySelector("[data-drawer-qty-input]");
    if (!(input instanceof HTMLInputElement)) return;
    const value = Math.max(1, Math.floor(Number(input.value) || 1));
    input.value = String(value);
  }

  /** Mirrors the selected frequency's price into the summary header. */
  #syncHeaderPrice() {
    const selected = this.#getSelectedRadio();
    if (!selected) return;

    const priceEl = this.#els.body.querySelector("[data-drawer-price]");
    const compareEl = this.#els.body.querySelector("[data-drawer-compare]");

    if (priceEl instanceof HTMLElement && selected.dataset.price) {
      priceEl.textContent = selected.dataset.price;
    }
    if (compareEl instanceof HTMLElement) {
      const hasDiscount = selected.dataset.hasDiscount === "true";
      if (hasDiscount && selected.dataset.compare) {
        compareEl.textContent = selected.dataset.compare;
        compareEl.classList.remove("tw:hidden");
      } else {
        compareEl.classList.add("tw:hidden");
      }
    }
  }

  /** @returns {HTMLInputElement | null} */
  #getSelectedRadio() {
    const checked = this.#els.body.querySelector('input[name="subscription-frequency"]:checked');
    return checked instanceof HTMLInputElement ? checked : null;
  }

  /** @returns {string[]} Section ids of every cart-items-component on the page. */
  #cartSectionIds() {
    return Array.from(document.querySelectorAll("cart-items-component"))
      .map(el => (el instanceof HTMLElement ? (el.dataset.sectionId ?? "") : ""))
      .filter(Boolean);
  }

  /** "Set up subscription": add a new subscription line or convert the existing line. */
  async confirm() {
    const ctx = this.#context;
    if (!ctx) return;

    const selected = this.#getSelectedRadio();
    const sellingPlanId = selected?.dataset.sellingPlanId;
    if (!sellingPlanId) {
      this.#setError(Theme?.translations?.error ?? "Please choose a delivery frequency.");
      return;
    }

    const quantity = this.#getQty();
    this.#setConfirmLoading(true);
    this.#setError("");

    try {
      if (ctx.mode === "convert") {
        await this.#convertLine(ctx.lineKey, quantity, sellingPlanId);
      } else {
        await this.#addLine(quantity, sellingPlanId);
      }
      this.closeDialog();
    } catch (error) {
      console.error("Subscription drawer: cart operation failed.", error);
      this.#setError(Theme?.translations?.error ?? "Something went wrong. Please try again.");
    } finally {
      this.#setConfirmLoading(false);
    }
  }

  /**
   * Adds a new subscription line. Mirrors product-form.js: requests the cart sections so
   * the cart drawer re-renders, then dispatches CartAddEvent (which auto-opens the drawer).
   * @param {number} quantity
   * @param {string} sellingPlanId
   */
  async #addLine(quantity, sellingPlanId) {
    // The section resolves the variant; read it back from refs.body (authoritative — it
    // reflects ?variant= when supplied, else the eligible fallback for hydrate cards).
    const variantId = this.#els.body.dataset.variantId || this.#context?.variantId;
    if (!variantId) throw new Error("No variant id to add");

    // Stamp the active portal_id onto the line item, mirroring product-form.js — the custom carrier
    // service reads line-item properties but not cart notes/attributes. PortalManager owns the
    // cookie/metafield resolution + validation so it stays in one place. (Reward properties are no
    // longer attached on add; the cart line reads BV/IBV/UC/SRP/cashback from its variant metafields.)
    let properties = {};
    const portalId = window.PortalManager?.getPortalId?.();
    if (portalId) properties._portal_id = portalId;
    properties = withDistributorIdProperty(properties, {
      isRastellis: this.#els.body.dataset.isRastellis === "true",
    });

    const item = { id: Number(variantId), quantity, selling_plan: Number(sellingPlanId) };
    if (Object.keys(properties).length) item.properties = properties;

    const items = [item];

    // BCRS fee: if the product has a paired deposit-fee variant, add it alongside
    // the main item. The fee variant id is surfaced on the product-form-component
    // data attribute (the PDP is the only page with subscription drawer context).
    const bcrsFeeVariantId = document.querySelector(
      'product-form-component[data-bcrs-fee-variant-id]'
    )?.dataset.bcrsFeeVariantId;
    if (bcrsFeeVariantId) {
      const { buildFeeItem } = await import('@theme/bcrs-fee');
      // Fee first: newest-first cart ordering puts the main line above its fee.
      items.unshift(buildFeeItem(bcrsFeeVariantId, variantId, quantity, sellingPlanId));
    }

    const sections = this.#cartSectionIds();
    const body = JSON.stringify({
      items,
      sections: sections.join(","),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig("json", { body }));
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || "Add failed");

    document.dispatchEvent(
      new CartAddEvent(data, this.id, {
        source: "subscription-drawer",
        itemCount: quantity,
        variantId: String(variantId),
        sections: data.sections,
      })
    );
  }

  /**
   * Converts an existing line to a subscription (or changes its frequency) via /cart/change.
   * Requests the cart sections and dispatches CartUpdateEvent so the open cart drawer refreshes.
   * @param {string} lineKey
   * @param {number} quantity
   * @param {string} sellingPlanId
   */
  async #convertLine(lineKey, quantity, sellingPlanId) {
    if (!lineKey) throw new Error("No line key to convert");

    const sections = this.#cartSectionIds();

    // BCRS fee: a paired deposit line records its parent's selling plan as a
    // property so it stays matched to one parent line. Converting the parent to a
    // new plan can't edit that property in place (/cart/change can't set line
    // properties), so the fee is re-homed — drop the stale fee and add a fresh one
    // recording the new plan. Find the existing fee from the open cart DOM (the
    // cart drawer is open during convert), matched on the line's CURRENT plan,
    // before anything changes.
    const bcrsFeeVariantId = this.#context?.bcrsFeeVariantId;
    let oldFeeKey = null;
    let mainVariantId = '';
    if (bcrsFeeVariantId) {
      try {
        const mainRow = document.querySelector(`tr[data-key="${CSS.escape(lineKey)}"]`);
        if (mainRow instanceof HTMLElement) {
          mainVariantId = mainRow.dataset.variantId || '';
          const container = mainRow.closest('cart-items-component');
          if (container && mainVariantId) {
            const { findPairedFeeRow } = await import('@theme/bcrs-fee');
            const feeRow = findPairedFeeRow(container, mainVariantId, mainRow.dataset.sellingPlan || '');
            if (feeRow instanceof HTMLElement && feeRow.dataset.key) oldFeeKey = feeRow.dataset.key;
          }
        }
      } catch (e) {
        console.warn('[bcrs-fee] Could not locate paired fee during convert', e);
      }
    }

    // Fee-paired path: do the fee swap first without section rendering, then
    // convert the main line last WITH sections, so the single resulting morph
    // lands on the final, correctly-paired state (converted line + a fee that
    // carries the new plan). No event is dispatched between the steps, so no
    // reconciliation runs mid-sequence to prune the not-yet-paired fee.
    if (bcrsFeeVariantId && mainVariantId) {
      const { buildFeeItem, visibleItemCount } = await import('@theme/bcrs-fee');

      if (oldFeeKey) await this.#postChange(JSON.stringify({ id: oldFeeKey, quantity: 0 }));

      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig("json", {
          body: JSON.stringify({ items: [buildFeeItem(bcrsFeeVariantId, mainVariantId, quantity, sellingPlanId)] }),
        })
      );
      const addData = await addResponse.json();
      if (addData.status) throw new Error(addData.description || addData.message || "Fee add failed");

      const cart = await this.#postChange(
        JSON.stringify({
          id: lineKey,
          quantity,
          selling_plan: Number(sellingPlanId),
          sections: sections.join(","),
          sections_url: window.location.pathname,
        })
      );
      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          source: "subscription-drawer",
          itemCount: visibleItemCount(cart),
          sections: cart.sections,
        })
      );
      return;
    }

    // No paired fee — plain convert.
    const cart = await this.#postChange(
      JSON.stringify({
        id: lineKey,
        quantity,
        selling_plan: Number(sellingPlanId),
        sections: sections.join(","),
        sections_url: window.location.pathname,
      })
    );
    document.dispatchEvent(
      new CartUpdateEvent(cart, this.id, { source: "subscription-drawer", sections: cart.sections })
    );
  }

  /**
   * @param {string} body
   * @returns {Promise<any>} the parsed cart
   */
  async #postChange(body) {
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig("json", { body }));
    const data = await response.json();
    if (data.status && data.status !== 200) throw new Error(data.description || data.message || "Change failed");
    return data;
  }

  /** @param {boolean} loading */
  #setBodyLoading(loading) {
    this.#els.loading?.toggleAttribute("hidden", !loading);
    this.#els.body?.toggleAttribute("hidden", loading);
  }

  /** @param {boolean} enabled */
  #setConfirmEnabled(enabled) {
    if (this.#els.confirmButton) this.#els.confirmButton.disabled = !enabled;
  }

  /** @param {boolean} loading */
  #setConfirmLoading(loading) {
    const { confirmButton, confirmText, confirmSpinner } = this.#els;
    if (confirmButton) {
      confirmButton.disabled = loading;
      confirmButton.setAttribute("aria-busy", loading ? "true" : "false");
    }
    confirmText?.classList.toggle("tw:hidden", loading);
    confirmSpinner?.classList.toggle("tw:hidden", !loading);
  }

  /** @param {string} message */
  #setError(message) {
    const { error } = this.#els;
    if (!error) return;
    error.textContent = message;
    error.toggleAttribute("hidden", !message);
  }
}

if (!customElements.get("subscription-drawer")) {
  customElements.define("subscription-drawer", SubscriptionDrawer);
}
